/** Game constants: planets, stars, systems, wormholes, archetypes, hazards, ruins, flora, rocks, environment. Part of src/constants.ts (re-exported there; import from '../constants'). */
import type { PlanetPalette, PlanetLayerProfile, CloudProfile, PlanetArchetype, HazardType, RuinType, FloraType, EnvironmentalEffectType, RockType, StarType, PlanetSizeCategory, SystemPreset, OrbitalZone, PlanetSizePreset, MapLayout, TerrainProfile, PlanetLayer } from '../types';

// =============================================================================
// PLANET PALETTES
// =============================================================================

export const PLANET_PALETTES: PlanetPalette[] = [
  { a: '#5a8a6a', b: '#a8d0a0', c: '#2a4a30', name: 'thalia' },
  { a: '#6a7a9a', b: '#a0b8d0', c: '#2a3a50', name: 'bruma' },
  { a: '#9a6a5a', b: '#d0a890', c: '#1a0a00', name: 'tephra' },
  { a: '#7a6a8a', b: '#b8a0c8', c: '#2a1a3a', name: 'murex' },
  { a: '#8a8a6a', b: '#c8c8a0', c: '#3a3a2a', name: 'pallor' },
  { a: '#6a8a8a', b: '#a0c8c0', c: '#1a3a3a', name: 'pelago' },
  { a: '#8a7060', b: '#c8a890', c: '#3a2810', name: 'ochrus' },
  { a: '#607060', b: '#98b098', c: '#1a3018', name: 'viridis' },
  { a: '#8a8a98', b: '#c8c8d8', c: '#3a3a48', name: 'sonora' },
  { a: '#4a8a5a', b: '#c8b888', c: '#0a2a5a', name: 'earth' },
];

// =============================================================================
// PLANET LAYERS — surface materials by height band (core/planetColors.ts)
// =============================================================================

export const PLANET_LAYERS: Record<PlanetArchetype | 'earth', PlanetLayerProfile> = {
  thalia: {   // temperate verdant: basins, grass, meadow, bare rock, snowcaps
    layers: [
      { name: 'basin',  color: '#2f4f3a', upTo: 0.16 },
      { name: 'grass',  color: '#5a8a6a', upTo: 0.58 },
      { name: 'meadow', color: '#9cc890', upTo: 0.82 },
      { name: 'scree',  color: '#8a8578', upTo: 0.93 },
      { name: 'snow',   color: '#e8ece6', upTo: 1 },
    ],
    rock: '#7a7468', rockSlope: [0.22, 0.5], crater: '#2a2018', mound: '#9cc890', breakup: 0.07,
  },
  bruma: {    // cold haze: dark ice, frost, pale ice, snow
    layers: [
      { name: 'darkIce', color: '#2a3a50', upTo: 0.2 },
      { name: 'frost',   color: '#6a7a9a', upTo: 0.55 },
      { name: 'ice',     color: '#a0b8d0', upTo: 0.8 },
      { name: 'snow',    color: '#dde6f0', upTo: 1 },
    ],
    rock: '#56607a', rockSlope: [0.25, 0.55], crater: '#1a2436', mound: '#a0b8d0', breakup: 0.05,
  },
  tephra: {   // volcanic ash: obsidian, ash, baked earth, dust, ember crust on the peaks
    layers: [
      { name: 'obsidian', color: '#1a0a00', upTo: 0.14 },
      { name: 'ash',      color: '#4a3830', upTo: 0.4 },
      { name: 'baked',    color: '#9a6a5a', upTo: 0.72 },
      { name: 'dust',     color: '#d0a890', upTo: 0.9 },
      { name: 'ember',    color: '#c86a3a', upTo: 1 },
    ],
    rock: '#3a2a24', rockSlope: [0.2, 0.45], crater: '#120800', mound: '#d0a890', breakup: 0.12,
  },
  murex: {    // twilight fungal: deep violet, mauve, lilac, pale caps
    layers: [
      { name: 'deep',  color: '#2a1a3a', upTo: 0.2 },
      { name: 'mauve', color: '#7a6a8a', upTo: 0.6 },
      { name: 'lilac', color: '#b8a0c8', upTo: 0.85 },
      { name: 'cap',   color: '#e0d0e8', upTo: 1 },
    ],
    rock: '#4a3c58', rockSlope: [0.25, 0.55], crater: '#1a0f26', mound: '#b8a0c8', breakup: 0.08,
  },
  pallor: {   // bleached steppe
    layers: [
      { name: 'shadow', color: '#3a3a2a', upTo: 0.15 },
      { name: 'steppe', color: '#8a8a6a', upTo: 0.55 },
      { name: 'bleach', color: '#c8c8a0', upTo: 0.85 },
      { name: 'chalk',  color: '#ece8d0', upTo: 1 },
    ],
    rock: '#6a6a58', rockSlope: [0.25, 0.55], crater: '#2a2a1c', mound: '#c8c8a0', breakup: 0.06,
  },
  pelago: {   // tidal mineral: wide shallows, mineral flats, pale salts
    layers: [
      { name: 'shallows', color: '#1a3a3a', upTo: 0.3 },
      { name: 'flats',    color: '#6a8a8a', upTo: 0.6 },
      { name: 'mineral',  color: '#a0c8c0', upTo: 0.85 },
      { name: 'salt',     color: '#d8f0e8', upTo: 1 },
    ],
    rock: '#4c6a68', rockSlope: [0.25, 0.55], crater: '#102626', mound: '#a0c8c0', breakup: 0.05,
  },
  ochrus: {   // arid mineral
    layers: [
      { name: 'shade', color: '#3a2810', upTo: 0.15 },
      { name: 'ochre', color: '#8a7060', upTo: 0.5 },
      { name: 'sand',  color: '#c8a890', upTo: 0.8 },
      { name: 'pale',  color: '#e8d8c0', upTo: 1 },
    ],
    rock: '#6a5440', rockSlope: [0.22, 0.5], crater: '#2a1a08', mound: '#c8a890', breakup: 0.09,
  },
  viridis: {  // dense overgrowth: mostly canopy, little bare ground
    layers: [
      { name: 'floor',  color: '#1a3018', upTo: 0.2 },
      { name: 'canopy', color: '#3e5c3a', upTo: 0.5 },
      { name: 'upper',  color: '#607060', upTo: 0.75 },
      { name: 'crown',  color: '#98b098', upTo: 1 },
    ],
    rock: '#5a6050', rockSlope: [0.3, 0.6], crater: '#101e0e', mound: '#607060', breakup: 0.1,
  },
  sonora: {   // resonant hollow: grey-violet stone
    layers: [
      { name: 'hollow', color: '#3a3a48', upTo: 0.2 },
      { name: 'stone',  color: '#8a8a98', upTo: 0.55 },
      { name: 'pale',   color: '#c8c8d8', upTo: 0.8 },
      { name: 'ring',   color: '#eceef4', upTo: 1 },
    ],
    rock: '#5c5c6c', rockSlope: [0.25, 0.55], crater: '#22222c', mound: '#c8c8d8', breakup: 0.04,
  },
  earth: {    // megaplanet earth: ocean, shore, grass, highland, snow
    layers: [
      { name: 'ocean',    color: '#0a2a5a', upTo: 0.3 },
      { name: 'shore',    color: '#c8b888', upTo: 0.36 },
      { name: 'grass',    color: '#4a8a5a', upTo: 0.7 },
      { name: 'highland', color: '#6a6a58', upTo: 0.88 },
      { name: 'snow',     color: '#f0f0f0', upTo: 1 },
    ],
    rock: '#5a5a4e', rockSlope: [0.22, 0.5], crater: '#2a2418', mound: '#4a8a5a', breakup: 0.03,
  },
};

/**
 * What lies under each surface: strata by depth below the generated terrain, as a
 * fraction of the planet radius (ascending upTo, last is 1). A crater that digs
 * through the topsoil shows these — and keeps showing them; the exposed material
 * is a map of everything that has happened to the planet.
 */
export const PLANET_STRATA: Record<PlanetArchetype | 'earth', PlanetLayer[]> = {
  thalia:  [{ name: 'topsoil', color: '#4a3a2a', upTo: 0.02 }, { name: 'clay', color: '#6a4a34', upTo: 0.06 }, { name: 'bedrock', color: '#5a5650', upTo: 0.14 }, { name: 'deep', color: '#3a3038', upTo: 1 }],
  bruma:   [{ name: 'packedIce', color: '#35506a', upTo: 0.02 }, { name: 'permafrost', color: '#2a3448', upTo: 0.06 }, { name: 'frozenRock', color: '#444c58', upTo: 0.14 }, { name: 'deep', color: '#2a2630', upTo: 1 }],
  tephra:  [{ name: 'ash', color: '#3a3230', upTo: 0.02 }, { name: 'tuff', color: '#5a4a44', upTo: 0.06 }, { name: 'basalt', color: '#2a2426', upTo: 0.14 }, { name: 'magma', color: '#8a3a1a', upTo: 1 }],
  murex:   [{ name: 'mycelium', color: '#4a3a5a', upTo: 0.02 }, { name: 'loam', color: '#3a2e44', upTo: 0.06 }, { name: 'shale', color: '#48445a', upTo: 0.14 }, { name: 'deep', color: '#2a2436', upTo: 1 }],
  pallor:  [{ name: 'chalk', color: '#c8c0a8', upTo: 0.02 }, { name: 'marl', color: '#a09880', upTo: 0.06 }, { name: 'limestone', color: '#8a8470', upTo: 0.14 }, { name: 'deep', color: '#5a5650', upTo: 1 }],
  pelago:  [{ name: 'silt', color: '#5a6a68', upTo: 0.02 }, { name: 'salt', color: '#9aa8a4', upTo: 0.06 }, { name: 'slate', color: '#4a5458', upTo: 0.14 }, { name: 'deep', color: '#2e3638', upTo: 1 }],
  ochrus:  [{ name: 'sand', color: '#b08a58', upTo: 0.02 }, { name: 'hardpan', color: '#8a6a44', upTo: 0.06 }, { name: 'sandstone', color: '#a0764c', upTo: 0.14 }, { name: 'iron', color: '#6a3e2a', upTo: 1 }],
  viridis: [{ name: 'humus', color: '#3a3020', upTo: 0.02 }, { name: 'roots', color: '#4a3a28', upTo: 0.06 }, { name: 'bedrock', color: '#5a5648', upTo: 0.14 }, { name: 'deep', color: '#30282a', upTo: 1 }],
  sonora:  [{ name: 'dust', color: '#5a5060', upTo: 0.02 }, { name: 'lattice', color: '#7a6aa0', upTo: 0.06 }, { name: 'hollowRock', color: '#3a3448', upTo: 0.14 }, { name: 'resonantCore', color: '#9a7ad0', upTo: 1 }],
  earth:   [{ name: 'soil', color: '#5a4632', upTo: 0.02 }, { name: 'clay', color: '#7a5a3c', upTo: 0.06 }, { name: 'bedrock', color: '#6a665c', upTo: 0.14 }, { name: 'mantle', color: '#4a3a36', upTo: 1 }],
};

// =============================================================================
// PLANET CLOUDS — per-archetype cloud layer (rendering/cloudLayer.ts); absent = clear skies
// =============================================================================

export const PLANET_CLOUDS: Partial<Record<PlanetArchetype | 'earth', CloudProfile>> = {
  thalia:  { style: 'puffs', color: '#f4f6f2', coverage: 0.42, altitude: 0.09, puffScale: 0.42, speed: 0.025, opacity: 0.92 },
  viridis: { style: 'puffs', color: '#e8f0e4', coverage: 0.55, altitude: 0.10, puffScale: 0.48, speed: 0.02,  opacity: 0.9 },
  earth:   { style: 'puffs', color: '#f8f8f8', coverage: 0.5,  altitude: 0.06, puffScale: 0.4,  speed: 0.03,  opacity: 0.95 },
  pelago:  { style: 'puffs', color: '#e4f4f0', coverage: 0.38, altitude: 0.08, puffScale: 0.4,  speed: 0.035, opacity: 0.85 },
  bruma:   { style: 'haze',  color: '#c8d4e8', coverage: 0.5,  altitude: 0.07, puffScale: 0.4,  speed: 0.015, opacity: 0.55 },
  tephra:  { style: 'puffs', color: '#5a4a44', coverage: 0.28, altitude: 0.08, puffScale: 0.5,  speed: 0.05,  opacity: 0.85 },
  murex:   { style: 'haze',  color: '#c0a0d8', coverage: 0.35, altitude: 0.1,  puffScale: 0.4,  speed: 0.02,  opacity: 0.4 },
  ochrus:  { style: 'haze',  color: '#d8b890', coverage: 0.25, altitude: 0.05, puffScale: 0.4,  speed: 0.04,  opacity: 0.35 },
  pallor:  { style: 'puffs', color: '#eeeadc', coverage: 0.18, altitude: 0.08, puffScale: 0.36, speed: 0.03,  opacity: 0.8 },
  // sonora: clear
};

// =============================================================================
// TERRAIN PROFILES — archetype-specific terrain generation parameters
// =============================================================================

export const TERRAIN_PROFILES: Record<PlanetArchetype, TerrainProfile> = {
  thalia: {
    octaves: [{ freq: 3.0, amp: 0.40 }, { freq: 5.5, amp: 0.30 }, { freq: 9.0, amp: 0.15 }],
    ridgeFraction: 0, plateauFraction: 0, jitter: 0.06, amplitude: 0.10,
    stamps: [
      { type: 'valley', angularSize: [0.4, 0.8], amplitude: [0.04, 0.08], count: [0, 1] },
      { type: 'mesa', angularSize: [0.2, 0.4], amplitude: [0.03, 0.06], count: [0, 2] },
      { type: 'basin', angularSize: [0.3, 0.6], amplitude: [0.02, 0.04], count: [0, 1] },
    ],
  },
  bruma: {
    octaves: [{ freq: 2.0, amp: 0.50 }, { freq: 4.0, amp: 0.25 }],
    ridgeFraction: 0, plateauFraction: 0.6, jitter: 0.04, amplitude: 0.07,
    stamps: [
      { type: 'valley', angularSize: [0.3, 0.7], amplitude: [0.03, 0.06], count: [0, 1] },
      { type: 'ridge', angularSize: [0.3, 0.6], amplitude: [0.03, 0.07], count: [0, 1] },
      { type: 'basin', angularSize: [0.4, 0.8], amplitude: [0.02, 0.05], count: [0, 1] },
    ],
  },
  tephra: {
    octaves: [{ freq: 4.0, amp: 0.30 }, { freq: 7.0, amp: 0.25 }, { freq: 11.0, amp: 0.20 }, { freq: 16.0, amp: 0.10 }],
    ridgeFraction: 0.7, plateauFraction: 0, jitter: 0.08, amplitude: 0.14,
    stamps: [
      { type: 'caldera', angularSize: [0.2, 0.5], amplitude: [0.06, 0.12], count: [1, 2] },
      { type: 'ridge', angularSize: [0.3, 0.7], amplitude: [0.04, 0.10], count: [0, 1] },
      { type: 'spireCluster', angularSize: [0.15, 0.3], amplitude: [0.05, 0.10], count: [0, 1] },
    ],
  },
  murex: {
    octaves: [{ freq: 3.5, amp: 0.35 }, { freq: 6.5, amp: 0.30 }, { freq: 10.0, amp: 0.15 }],
    ridgeFraction: 0.5, plateauFraction: 0, jitter: 0.02, amplitude: 0.10,
    stamps: [
      { type: 'canyon', angularSize: [0.3, 0.6], amplitude: [0.04, 0.08], count: [0, 1] },
      { type: 'ridge', angularSize: [0.3, 0.6], amplitude: [0.03, 0.07], count: [0, 1] },
      { type: 'spireCluster', angularSize: [0.1, 0.25], amplitude: [0.04, 0.08], count: [0, 1] },
    ],
  },
  pallor: {
    octaves: [{ freq: 2.5, amp: 0.40 }, { freq: 5.0, amp: 0.20 }],
    ridgeFraction: 0, plateauFraction: 0.3, jitter: 0.03, amplitude: 0.05,
    stamps: [
      { type: 'crater', angularSize: [0.15, 0.35], amplitude: [0.03, 0.06], count: [1, 3] },
      { type: 'mesa', angularSize: [0.2, 0.4], amplitude: [0.02, 0.04], count: [0, 1] },
      { type: 'basin', angularSize: [0.3, 0.5], amplitude: [0.02, 0.03], count: [0, 1] },
    ],
  },
  pelago: {
    octaves: [{ freq: 3.0, amp: 0.35 }, { freq: 6.0, amp: 0.25 }, { freq: 10.0, amp: 0.20 }],
    ridgeFraction: 0.3, plateauFraction: 0, jitter: 0.05, amplitude: 0.08,
    stamps: [
      { type: 'mesa', angularSize: [0.15, 0.3], amplitude: [0.04, 0.08], count: [0, 2] },
      { type: 'basin', angularSize: [0.3, 0.6], amplitude: [0.02, 0.04], count: [0, 1] },
    ],
  },
  ochrus: {
    octaves: [{ freq: 2.5, amp: 0.45 }, { freq: 8.0, amp: 0.30 }],
    ridgeFraction: 0.8, plateauFraction: 0.5, jitter: 0.04, amplitude: 0.12,
    stamps: [
      { type: 'canyon', angularSize: [0.3, 0.7], amplitude: [0.05, 0.10], count: [0, 1] },
      { type: 'mesa', angularSize: [0.2, 0.4], amplitude: [0.03, 0.06], count: [0, 1] },
      { type: 'ridge', angularSize: [0.2, 0.5], amplitude: [0.04, 0.08], count: [0, 1] },
    ],
  },
  viridis: {
    octaves: [{ freq: 3.5, amp: 0.35 }, { freq: 6.0, amp: 0.30 }, { freq: 9.5, amp: 0.15 }],
    ridgeFraction: 0, plateauFraction: 0, jitter: 0.10, amplitude: 0.08,
    stamps: [
      { type: 'valley', angularSize: [0.4, 0.8], amplitude: [0.03, 0.06], count: [0, 1] },
      { type: 'ridge', angularSize: [0.3, 0.6], amplitude: [0.03, 0.06], count: [0, 1] },
      { type: 'basin', angularSize: [0.3, 0.6], amplitude: [0.02, 0.04], count: [0, 1] },
    ],
  },
  sonora: {
    octaves: [{ freq: 2.0, amp: 0.50 }, { freq: 5.0, amp: 0.15 }],
    ridgeFraction: 0, plateauFraction: 0, jitter: 0.01, amplitude: 0.03,
    stamps: [
      { type: 'ridge', angularSize: [0.3, 0.6], amplitude: [0.01, 0.03], count: [0, 1] },
      { type: 'basin', angularSize: [0.4, 0.8], amplitude: [0.01, 0.02], count: [0, 1] },
    ],
  },
};

// =============================================================================
// ARCHETYPE GLOW — per-archetype atmosphere tint and opacity
// =============================================================================

export const ARCHETYPE_GLOW: Partial<Record<PlanetArchetype, { color: string; opacity: number }>> = {
  tephra: { color: '#d08040', opacity: 0.07 },
  bruma:  { color: '#6090d0', opacity: 0.06 },
  murex:  { color: '#9060c0', opacity: 0.05 },
  sonora: { color: '#c8c8d8', opacity: 0.03 },
};

// =============================================================================
// STARS
// =============================================================================

export const STAR_DENSITY = 8.0;
export const STAR_RADIUS_MIN = 4.0;
export const STAR_RADIUS_MAX = 6.0;
export const STAR_MIN_PLANET_DISTANCE_MULT = 3.0; // star.radius × this = inner orbit
export const STAR_COLORS = [
  { core: '#fff4e0', corona: '#ffaa44', name: 'Sol' },
  { core: '#e0e8ff', corona: '#8888ff', name: 'Vega' },
  { core: '#ffe0d0', corona: '#ff6644', name: 'Antares' },
];

// =============================================================================
// STAR TYPES — campaign map generation
// =============================================================================

export interface StarTypeConfig {
  name: string;
  description: string;
  planetCountRange: [number, number];
  starRadiusRange: [number, number];
  starDensity: number;
  coreColor: string;
  coronaColor: string;
}

export const STAR_TYPES: Record<StarType, StarTypeConfig> = {
  redDwarf: {
    name: 'Red Dwarf',
    description: 'Dim and cool. Small, frozen or volcanic worlds huddle close.',
    planetCountRange: [3, 7],
    starRadiusRange: [2.5, 3.5],
    starDensity: 10,
    coreColor: '#ff6644',
    coronaColor: '#cc3322',
  },
  orangeDwarf: {
    name: 'Orange Dwarf',
    description: 'Stable and temperate. Supports diverse biomes and ocean worlds.',
    planetCountRange: [5, 10],
    starRadiusRange: [3.5, 5.0],
    starDensity: 8.5,
    coreColor: '#ffaa44',
    coronaColor: '#dd7722',
  },
  yellowDwarf: {
    name: 'Yellow Dwarf',
    description: 'Sol-type. Balanced system with all biomes represented.',
    planetCountRange: [6, 12],
    starRadiusRange: [4.0, 6.0],
    starDensity: 8.0,
    coreColor: '#fff4e0',
    coronaColor: '#ffaa44',
  },
  blueGiant: {
    name: 'Blue Giant',
    description: 'Blazing and short-lived. Harsh radiation favors volcanic and mineral worlds.',
    planetCountRange: [4, 8],
    starRadiusRange: [5.0, 8.0],
    starDensity: 6.0,
    coreColor: '#c0d8ff',
    coronaColor: '#6688ff',
  },
  redGiant: {
    name: 'Red Giant',
    description: 'Dying star. Bloated and cool. Few large, desolate worlds remain.',
    planetCountRange: [2, 5],
    starRadiusRange: [7.0, 10.0],
    starDensity: 3.0,
    coreColor: '#ff8844',
    coronaColor: '#cc4422',
  },
};

export const BIOME_AFFINITY: Record<StarType, Record<PlanetArchetype, number>> = {
  redDwarf:    { thalia: 1, bruma: 5, tephra: 4, murex: 2, pallor: 4, pelago: 0, ochrus: 2, viridis: 1, sonora: 2 },
  orangeDwarf: { thalia: 5, bruma: 2, tephra: 1, murex: 2, pallor: 1, pelago: 4, ochrus: 2, viridis: 5, sonora: 2 },
  yellowDwarf: { thalia: 3, bruma: 3, tephra: 3, murex: 3, pallor: 2, pelago: 3, ochrus: 3, viridis: 3, sonora: 2 },
  blueGiant:   { thalia: 1, bruma: 1, tephra: 5, murex: 4, pallor: 2, pelago: 0, ochrus: 4, viridis: 0, sonora: 3 },
  redGiant:    { thalia: 0, bruma: 4, tephra: 1, murex: 2, pallor: 5, pelago: 0, ochrus: 4, viridis: 0, sonora: 3 },
};

export const ORBITAL_BIOME_MODIFIERS: Record<OrbitalZone, Record<PlanetArchetype, number>> = {
  inner:  { thalia: 0.3, bruma: 0.0, tephra: 1.8, murex: 1.2, pallor: 0.5, pelago: 0.2, ochrus: 1.5, viridis: 0.3, sonora: 0.8 },
  middle: { thalia: 1.5, bruma: 0.3, tephra: 0.5, murex: 1.0, pallor: 0.5, pelago: 1.8, ochrus: 1.0, viridis: 1.5, sonora: 1.0 },
  outer:  { thalia: 0.5, bruma: 1.8, tephra: 0.2, murex: 0.8, pallor: 1.5, pelago: 0.3, ochrus: 0.5, viridis: 0.5, sonora: 1.2 },
};

export const PLANET_SIZE_WEIGHTS: Record<StarType, Record<PlanetSizeCategory, number>> = {
  redDwarf:    { tiny: 50, small: 30, medium: 15, large: 5,  massive: 0  },
  orangeDwarf: { tiny: 15, small: 30, medium: 35, large: 15, massive: 5  },
  yellowDwarf: { tiny: 10, small: 20, medium: 30, large: 25, massive: 15 },
  blueGiant:   { tiny: 5,  small: 10, medium: 25, large: 35, massive: 25 },
  redGiant:    { tiny: 0,  small: 10, medium: 30, large: 40, massive: 20 },
};

export const SIZE_CATEGORY_RADIUS: Record<PlanetSizeCategory, [number, number]> = {
  tiny:    [1.5, 3],
  small:   [3, 5],
  medium:  [5, 8],
  large:   [8, 12],
  massive: [12, 16],
};

// =============================================================================
// SYSTEM PRESETS
// =============================================================================

export interface SystemPresetConfig {
  name: string;
  description: string;
  starType: StarType;
  numStars: number;
  numPlanets: number;
  numWormholePairs: number;
  planetSize: PlanetSizePreset;
  mapLayout: MapLayout;
  hazardDensity: 'none' | 'some' | 'many';
}

export const SYSTEM_PRESETS: Record<SystemPreset, SystemPresetConfig | null> = {
  custom: null,
  solStandard: {
    name: 'Sol Standard',
    description: 'A balanced yellow dwarf system. Diverse biomes, moderate hazards.',
    starType: 'yellowDwarf',
    numStars: 1,
    numPlanets: 6,
    numWormholePairs: 0,
    planetSize: 'mixed',
    mapLayout: 'random',
    hazardDensity: 'some',
  },
  emberCluster: {
    name: 'Ember Cluster',
    description: 'A dim red dwarf with small, frozen and volcanic worlds in a tight ring.',
    starType: 'redDwarf',
    numStars: 1,
    numPlanets: 5,
    numWormholePairs: 0,
    planetSize: 'small',
    mapLayout: 'ring',
    hazardDensity: 'many',
  },
  twinSuns: {
    name: 'Twin Suns',
    description: 'Binary orange dwarfs with planets scattered between them.',
    starType: 'orangeDwarf',
    numStars: 2,
    numPlanets: 8,
    numWormholePairs: 0,
    planetSize: 'mixed',
    mapLayout: 'ring',
    hazardDensity: 'some',
  },
  furnace: {
    name: 'The Furnace',
    description: 'A blazing blue giant. Large, hostile worlds baked in radiation.',
    starType: 'blueGiant',
    numStars: 1,
    numPlanets: 5,
    numWormholePairs: 0,
    planetSize: 'large',
    mapLayout: 'linear',
    hazardDensity: 'many',
  },
  dyingLight: {
    name: 'Dying Light',
    description: 'A bloated red giant. Few massive, desolate worlds remain.',
    starType: 'redGiant',
    numStars: 1,
    numPlanets: 3,
    numWormholePairs: 0,
    planetSize: 'large',
    mapLayout: 'linear',
    hazardDensity: 'some',
  },
  grandOrrery: {
    name: 'Grand Orrery',
    description: 'A Sol-type star with a vast system of twelve worlds.',
    starType: 'yellowDwarf',
    numStars: 1,
    numPlanets: 12,
    numWormholePairs: 0,
    planetSize: 'mixed',
    mapLayout: 'random',
    hazardDensity: 'some',
  },
  theGauntlet: {
    name: 'The Gauntlet',
    description: 'Three red dwarfs in a layered field. Small worlds, many hazards.',
    starType: 'redDwarf',
    numStars: 3,
    numPlanets: 10,
    numWormholePairs: 0,
    planetSize: 'small',
    mapLayout: 'layered',
    hazardDensity: 'many',
  },
  megaplanet: {
    name: 'Megaplanet',
    description: 'One enormous planet. Close-quarters combat on a massive world.',
    starType: 'yellowDwarf',
    numStars: 0,
    numPlanets: 1,
    numWormholePairs: 0,
    planetSize: 'megaplanet',
    mapLayout: 'random',
    hazardDensity: 'many',
  },
  earth: {
    name: 'Earth',
    description: 'A single megaplanet with Earth-like continents. Blue oceans, green land, brown peaks.',
    starType: 'yellowDwarf',
    numStars: 0,
    numPlanets: 1,
    numWormholePairs: 0,
    planetSize: 'megaplanet',
    mapLayout: 'random',
    hazardDensity: 'some',
  },
};

export const MULTI_STAR_LAYOUT_DEFAULTS: Record<number, MapLayout> = {
  1: 'random',
  2: 'ring',
  3: 'layered',
};

// =============================================================================
// WORMHOLES
// =============================================================================

export const WORMHOLE_RADIUS = 2.5;
export const WORMHOLE_DENSITY = 8.0;
export const WORMHOLE_SPEED_BOOST = 1.3;
export const MAX_WORMHOLE_TRANSITS = 3;
export const WORMHOLE_COOLDOWN_DIST = 3.0; // radius multiplier to clear cooldown
export const WORMHOLE_COLORS = [
  { core: '#0a0a20', ring: '#7744dd', glow: '#4422aa', name: 'Rift' },
];

// =============================================================================
// PLANET ARCHETYPES
// =============================================================================

export interface RuinWeight {
  type: RuinType;
  weight: number;  // relative probability (higher = more likely)
}

export interface FloraWeight {
  type: FloraType;
  weight: number;
}

export interface RockWeight {
  type: RockType;
  weight: number;
}

export interface EffectWeight {
  type: EnvironmentalEffectType;
  weight: number;
}

export interface ArchetypeConfig {
  densityRange: [number, number];
  eligibleHazards: HazardType[];
  eligibleRuins: RuinWeight[];
  eligibleFlora: FloraWeight[];
  eligibleRocks: RockWeight[];
  eligibleEffects: EffectWeight[];
}

export const PLANET_ARCHETYPES: Record<PlanetArchetype, ArchetypeConfig> = {
  thalia:  { densityRange: [0.4, 1.0],  eligibleHazards: [],
    eligibleRuins: [
      { type: 'habitat', weight: 3 }, { type: 'surveyStation', weight: 2 },
      { type: 'landingPad', weight: 2 }, { type: 'supplyCache', weight: 1 },
      { type: 'antenna', weight: 1 },
    ],
    eligibleFlora: [
      { type: 'velvetMatgrass', weight: 2 }, { type: 'canopyOak', weight: 4 },
    ],
    eligibleRocks: [
      { type: 'alabaster', weight: 4 },
    ],
    eligibleEffects: [
      { type: 'driftwingFlock', weight: 2 },
    ] },
  bruma:   { densityRange: [0.5, 1.2],  eligibleHazards: ['geyser'],
    eligibleRuins: [
      { type: 'weatherStation', weight: 3 }, { type: 'surveyStation', weight: 2 },
      { type: 'antenna', weight: 2 }, { type: 'supplyCache', weight: 1 },
    ],
    eligibleFlora: [
      { type: 'ghostReed', weight: 2 }, { type: 'spirePine', weight: 4 },
      { type: 'poolskater', weight: 1 },
    ],
    eligibleRocks: [
      { type: 'alabaster', weight: 4 }, { type: 'basalt', weight: 2 },
    ],
    eligibleEffects: [
      { type: 'aurora', weight: 2 },
    ] },
  tephra:  { densityRange: [0.6, 1.2],  eligibleHazards: ['volcanic'],
    eligibleRuins: [
      { type: 'refinery', weight: 3 }, { type: 'landingPad', weight: 2 },
      { type: 'supplyCache', weight: 1 },
    ],
    eligibleFlora: [
      { type: 'thermocapLichen', weight: 3 }, { type: 'frondPalm', weight: 2 },
    ],
    eligibleRocks: [
      { type: 'sandstone', weight: 3 }, { type: 'basalt', weight: 1 },
    ],
    eligibleEffects: [
      { type: 'thermalcrawlerSwarm', weight: 2 },
    ] },
  murex:   { densityRange: [0.3, 0.8],  eligibleHazards: ['crystal'],
    eligibleRuins: [
      { type: 'surveyStation', weight: 3 }, { type: 'refinery', weight: 2 },
      { type: 'antenna', weight: 1 },
    ],
    eligibleFlora: [
      { type: 'lanternBracket', weight: 3 },
    ],
    eligibleRocks: [
      { type: 'basalt', weight: 4 }, { type: 'alabaster', weight: 2 },
    ],
    eligibleEffects: [] },
  pallor:  { densityRange: [0.4, 1.0],  eligibleHazards: [],
    eligibleRuins: [
      { type: 'landingPad', weight: 2 }, { type: 'supplyCache', weight: 2 },
      { type: 'habitat', weight: 2 }, { type: 'antenna', weight: 1 },
      { type: 'weatherStation', weight: 1 },
    ],
    eligibleFlora: [
      { type: 'ghostReed', weight: 3 },
    ],
    eligibleRocks: [
      { type: 'basalt', weight: 4 }, { type: 'sandstone', weight: 2 },
    ],
    eligibleEffects: [
      { type: 'driftwingFlock', weight: 1 }, { type: 'aurora', weight: 2 },
    ] },
  pelago:  { densityRange: [0.4, 0.9],  eligibleHazards: ['geyser'],
    eligibleRuins: [
      { type: 'weatherStation', weight: 3 }, { type: 'refinery', weight: 2 },
      { type: 'antenna', weight: 1 }, { type: 'surveyStation', weight: 1 },
    ],
    eligibleFlora: [
      { type: 'copperthread', weight: 3 }, { type: 'frondPalm', weight: 2 },
      { type: 'poolskater', weight: 1 },
    ],
    eligibleRocks: [
      { type: 'alabaster', weight: 4 },
    ],
    eligibleEffects: [] },
  ochrus:  { densityRange: [0.5, 1.1],  eligibleHazards: [],
    eligibleRuins: [
      { type: 'refinery', weight: 3 }, { type: 'supplyCache', weight: 2 },
      { type: 'landingPad', weight: 2 }, { type: 'antenna', weight: 1 },
    ],
    eligibleFlora: [
      { type: 'thermocapLichen', weight: 3 },
    ],
    eligibleRocks: [],
    eligibleEffects: [
      { type: 'thermalcrawlerSwarm', weight: 2 },
    ] },
  viridis: { densityRange: [0.3, 0.9],  eligibleHazards: [],
    eligibleRuins: [
      { type: 'habitat', weight: 3 }, { type: 'surveyStation', weight: 2 },
      { type: 'weatherStation', weight: 2 }, { type: 'supplyCache', weight: 1 },
    ],
    eligibleFlora: [
      { type: 'velvetMatgrass', weight: 2 }, { type: 'spirePine', weight: 3 }, { type: 'canopyOak', weight: 3 },
    ],
    eligibleRocks: [
      { type: 'sandstone', weight: 4 }, { type: 'alabaster', weight: 2 },
    ],
    eligibleEffects: [
      { type: 'driftwingFlock', weight: 2 },
    ] },
  sonora:  { densityRange: [0.08, 0.2], eligibleHazards: ['crystal'],
    eligibleRuins: [
      { type: 'antenna', weight: 3 }, { type: 'surveyStation', weight: 2 },
    ],
    eligibleFlora: [],
    eligibleRocks: [
      { type: 'alabaster', weight: 4 }, { type: 'basalt', weight: 2 },
    ],
    eligibleEffects: [
      { type: 'aurora', weight: 1 },
    ] },
};

// =============================================================================
// HAZARDS
// =============================================================================

export const HAZARD_HEALTH: Record<HazardType, number> = {
  volcanic: 50,
  geyser: 30,
  crystal: 40,
};

export const HAZARD_COUNTS: Record<string, { min: number; max: number }> = {
  none: { min: 0, max: 0 },
  some: { min: 0, max: 2 },
  many: { min: 1, max: 3 },
};

export const HAZARD_MIN_SPAWN_ANGLE = 0.5;  // radians from player spawn

// Volcanic eruption
export const VOLCANIC_ERUPTION_CHANCE = 0.10;
export const VOLCANIC_ERUPTION_DAMAGE = 20;
export const VOLCANIC_ERUPTION_RADIUS = 2.5;
export const VOLCANIC_ERUPTION_KNOCKBACK = 8.0;
export const VOLCANIC_PROXIMITY_DAMAGE = 3;
export const VOLCANIC_PROXIMITY_RADIUS = 2.0;

// Geyser boost
export const GEYSER_BOOST_RADIUS = 2.0;
export const GEYSER_BOOST_VELOCITY = 3.5;
export const GEYSER_PROXIMITY_RADIUS = 1.5;
export const GEYSER_LAUNCH_CHANCE = 0.20;
export const GEYSER_LAUNCH_VELOCITY = 5.0;

// Crystal shatter
export const CRYSTAL_SHATTER_DAMAGE = 15;
export const CRYSTAL_SHATTER_RADIUS = 2.5;

// Sonora resonance
export const SONORA_CRATER_MULTIPLIER = 1.5;
export const SONORA_KNOCKBACK_MULTIPLIER = 1.3;

// =============================================================================
// RUINS — pre-existing neutral structures
// =============================================================================

/** All ruin types — default fallback when archetype has no eligibleRuins */
export const ALL_RUIN_TYPES: RuinWeight[] = [
  { type: 'antenna', weight: 1 }, { type: 'refinery', weight: 1 },
  { type: 'surveyStation', weight: 1 }, { type: 'landingPad', weight: 1 },
  { type: 'supplyCache', weight: 1 }, { type: 'weatherStation', weight: 1 },
  { type: 'habitat', weight: 1 }, { type: 'commsRelay', weight: 1 },
];

export const RUIN_COUNTS: Record<string, { min: number; max: number }> = {
  none: { min: 0, max: 0 },
  few:  { min: 0, max: 1 },
  some: { min: 0, max: 2 },
  many: { min: 1, max: 3 },
};

export const RUIN_MIN_SPAWN_ANGLE = 0.5;  // radians from player/hazard

// =============================================================================
// FLORA & FAUNA
// =============================================================================

export const FLORA_COUNTS: Record<string, { min: number; max: number }> = {
  none: { min: 0, max: 0 },
  some: { min: 3, max: 7 },
  many: { min: 5, max: 10 },
};

export const FLORA_MIN_SPAWN_ANGLE = 0.4;  // radians from player/hazard/ruin/other flora

// =============================================================================
// ROCKS — planet surface scenery
// =============================================================================

export const ROCK_COUNTS: Record<string, { min: number; max: number }> = {
  none: { min: 0, max: 0 },
  some: { min: 3, max: 6 },
  many: { min: 5, max: 10 },
};

export const ROCK_MIN_SPAWN_ANGLE = 0.25;  // radians — rocks can be closer together than flora

export const ROCK_PALETTES = {
  basalt: {
    base: '#1a1210',
    mid: '#2a1c18',
    light: '#3a2820',
    crack: '#cc4010',
  },
  alabaster: {
    base: '#c8c0b4',
    mid: '#aba498',
    light: '#dad4c8',
    crack: '#8a8478',
  },
  sandstone: {
    base: '#8a7460',
    mid: '#7a6450',
    light: '#a08a70',
    crack: '#5a4a3a',
  },
} as const;

// =============================================================================
// ENVIRONMENTAL EFFECTS
// =============================================================================

export const EFFECT_COUNTS: Record<string, { min: number; max: number }> = {
  none: { min: 0, max: 0 },
  some: { min: 0, max: 1 },
  many: { min: 0, max: 2 },
};

export const PLANET_RING_CHANCE = 0.15;  // 15% chance per planet

// =============================================================================
// TERRAIN GENERATOR VERSION (R13, lane A)
// =============================================================================

/**
 * The terrain generator's version. Bump it in the same commit as any change to what core/mapgen.ts generates
 * for a given seed (heights, vertex count, profiles) — tests/terrainGeneratorVersion.test.ts holds a few planets'
 * fingerprints to it, the way BATTLE_VERSION's rule holds match results. Scar logs record it (sector/save.ts),
 * so a save that no longer fits says "the generator changed, version 1 → 2" rather than only a fingerprint.
 *  1 — the generator as it stood when the version was introduced (Oct 10 2026).
 */
export const TERRAIN_GENERATOR_VERSION = 1;
